import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bookmark, CheckCircle2, GraduationCap, ArrowRight } from 'lucide-react';

/** Compact, reusable child card used on Parent Home and the My Children page. */
export default function ChildCard({ child, bookings, schoolName }) {
  const navigate = useNavigate();
  const childBookings = (bookings || []).filter(b => b.child_id === child.id);
  const completedCount = childBookings.filter(b => ['completed', 'awaiting_feedback'].includes(b.status)).length;
  const savedCount = (child.saved_career_ids || []).length;
  const interests = child.interests || [];

  return (
    <div
      className="min-w-0 rounded-2xl border border-[#EFF0F2] bg-white p-5 transition-all hover:border-[#E0E2E6] hover:shadow-[0_2px_14px_rgba(16,24,40,0.06)]"
      data-testid={`child-card-${child.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#FFF0ED] flex items-center justify-center shrink-0 text-base font-bold text-[#F97D6A]">
          {child.full_name?.charAt(0)?.toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-bold text-[#1F2937] font-['Outfit'] truncate" data-testid={`child-name-${child.id}`}>
              {child.full_name}
            </p>
            {child.status !== 'approved' && (
              <Badge className="border-0 text-[9px] bg-amber-50 text-amber-700">{child.status}</Badge>
            )}
          </div>
          <p className="text-sm text-[#6B7280] mt-0.5 flex items-center gap-1.5 flex-wrap">
            <GraduationCap className="h-3.5 w-3.5 text-[#9CA3AF]" />
            Grade {child.grade}{child.division ? `-${child.division}` : ''}
            {(schoolName || child.school_name) && (
              <>
                <span className="text-[#D1D5DB]">·</span>
                <span className="truncate" data-testid={`child-school-${child.id}`}>{schoolName || child.school_name}</span>
              </>
            )}
          </p>
        </div>
      </div>

      {interests.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3" data-testid={`child-interests-${child.id}`}>
          {interests.slice(0, 4).map((i, j) => (
            <span key={j} className="text-[10px] font-medium bg-[#F5F6F8] text-[#4B5563] px-2 py-0.5 rounded-lg">{i}</span>
          ))}
          {interests.length > 4 && <span className="text-[10px] text-[#9CA3AF]">+{interests.length - 4}</span>}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 pt-3.5 border-t border-[#F3F4F6]">
        <span className="flex items-center gap-1.5 text-sm text-[#6B7280]" data-testid={`child-saved-${child.id}`}>
          <Bookmark className="h-3.5 w-3.5 text-[#9CA3AF]" />
          <span className="font-semibold text-[#1F2937]">{savedCount}</span> saved
        </span>
        <span className="flex items-center gap-1.5 text-sm text-[#6B7280]" data-testid={`child-completed-${child.id}`}>
          <CheckCircle2 className="h-3.5 w-3.5 text-[#9CA3AF]" />
          <span className="font-semibold text-[#1F2937]">{completedCount}</span> completed
        </span>
        <Button
          size="sm"
          onClick={() => navigate(`/parent/children/${child.id}`)}
          className="ml-auto bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-full h-8 px-3.5 text-xs font-semibold"
          data-testid={`view-journey-${child.id}`}
        >
          View Journey <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
}
